import {
  SURFACE_CLIENT_SYMBOL,
  SURFACE_CONTEXT,
  SURFACE_INITIAL,
  SURFACE_INTERACTION,
  SURFACE_RESULT,
  MAX_FLOW_SURFACE_HEIGHT_PX,
  MIN_CONTENT_SURFACE_WIDTH_PX,
  isPositiveSize,
  sizeFits,
} from './contract.js';

const OPERATION_TIMEOUT_MS = 2_000;
const INITIAL_CONTENT_RENDER_TIMEOUT_MS = 5_000;
const MRAID_INTERSTITIAL = 'interstitial';
const MANAGED_STYLE_PROPS = Object.freeze([
  'display', 'width', 'height', 'maxWidth', 'maxHeight', 'minWidth', 'minHeight',
  'position', 'inset', 'top', 'right', 'bottom', 'left', 'zIndex', 'overflow',
]);

export async function createSurfaceController(renderEnv) {
  const controller = new SurfaceController(renderEnv);
  await controller.ready();
  return controller;
}

export class SurfaceController {
  constructor(renderEnv) {
    this.adapter = createAdapter(renderEnv);
    this.requirements = null;
    this.wrapper = null;
    this.destroyed = false;
    this.listeners = new Set();
    this.operation = null;
    this.removeAdapterListener = this.adapter.onStateChange?.(state => {
      for (const listener of this.listeners) listener(state);
    }) ?? null;
  }

  async ready() { await this.adapter.ready?.(); }
  capabilities() { return this.adapter.capabilities(); }

  canSatisfy(requirements, size) {
    const caps = this.capabilities();
    if (!requirements) return false;
    if (requirements.initial !== SURFACE_INITIAL.Viewport && !isPositiveSize(size)) return false;
    const target = requirements.initial === SURFACE_INITIAL.Content
      ? responsiveContentSize(size, caps.maximum_size)
      : size;
    const initialSatisfied = requirements.initial === SURFACE_INITIAL.Allocated
      ? sizeFits(caps.current_size, target)
      : requirements.initial === SURFACE_INITIAL.Content
        ? !!target && (sizeFits(caps.current_size, target)
          || (caps.supports_content_resize && sizeFits(caps.maximum_size, target)))
        : requirements.initial === SURFACE_INITIAL.Viewport && caps.supports_viewport;
    if (!initialSatisfied) return false;
    if (requirements.interaction === SURFACE_INTERACTION.Fixed) return true;
    if (requirements.interaction === SURFACE_INTERACTION.ExpandPush)
      return caps.supports_push_expansion;
    if (requirements.interaction === SURFACE_INTERACTION.ExpandOverlay)
      return caps.supports_overlay_expansion;
    return false;
  }

  async prepare(requirements, size) {
    if (this.destroyed || !this.canSatisfy(requirements, size)) return false;
    const target = requirements.initial === SURFACE_INITIAL.Content
      ? responsiveContentSize(size, this.capabilities().maximum_size)
      : size;
    const prepared = await this.adapter.prepare(requirements.initial, target);
    if (prepared) this.requirements = requirements;
    return prepared;
  }

  bind(wrapper) {
    if (this.destroyed || !this.requirements)
      throw new Error('SurfaceController must be prepared before binding');
    if (this.wrapper && this.wrapper !== wrapper) delete this.wrapper[SURFACE_CLIENT_SYMBOL];
    this.wrapper = wrapper;
    const client = Object.freeze({
      context: this.adapter.context,
      environment: this.adapter.environment,
      capacity: () => this.adapter.capacity(),
      expand: request => this.expand(request),
      collapse: () => this.collapse(),
      subscribe: listener => this.subscribe(listener),
    });
    Object.defineProperty(wrapper, SURFACE_CLIENT_SYMBOL, { configurable: true, value: client });
    this.adapter.bindLifecycle?.(() => this.destroy());
    return client;
  }

  async verify(size) {
    if (this.destroyed || !this.requirements || !this.wrapper) return false;
    const target = this.requirements.initial === SURFACE_INITIAL.Content
      ? responsiveContentSize(size, this.capabilities().maximum_size)
      : size;
    return this.adapter.verify?.(this.requirements.initial, target, this.wrapper) ?? true;
  }

  async expand(request) {
    if (this.destroyed || !this.requirements || !validExpansionRequest(request))
      return failed('invalid-expand-request');
    return this.runOperation(() => this.adapter.expand(
      this.requirements.interaction, request, this.wrapper,
    ));
  }

  async collapse() {
    if (this.destroyed) return failed('surface-destroyed');
    return this.runOperation(() => this.adapter.collapse(this.wrapper));
  }

  async runOperation(operation) {
    if (this.operation) return failed('surface-operation-in-flight');
    const pending = Promise.resolve().then(operation);
    this.operation = pending;
    try { return await pending; }
    finally { if (this.operation === pending) this.operation = null; }
  }

  subscribe(listener) {
    if (typeof listener !== 'function') return () => {};
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.wrapper) delete this.wrapper[SURFACE_CLIENT_SYMBOL];
    this.removeAdapterListener?.();
    this.adapter.destroy();
    this.listeners.clear();
  }
}

function createAdapter(renderEnv) {
  if (renderEnv.isMraid) return new MraidAdapter();
  if (renderEnv.isSafeFrame) return new SafeFrameAdapter();
  if (renderEnv.inIframe && renderEnv.isFriendlyIframe) return new FriendlyFrameAdapter(renderEnv);
  if (!renderEnv.inIframe) return new SameDocumentAdapter();
  return new BlockedAdapter();
}

class DocumentContentAdapter {
  constructor() { this.pendingVerification = null; }

  async verify(initial, size, wrapper) {
    if (initial !== SURFACE_INITIAL.Content) return true;
    const check = () => {
      const actual = elementSize(wrapper);
      return !!actual
        && actual.width >= MIN_CONTENT_SURFACE_WIDTH_PX
        && actual.height >= size.height
        && !hasClippingAncestor(wrapper, actual);
    };
    if (check()) return true;

    const ownerWindow = wrapper.ownerDocument.defaultView ?? window;
    if (typeof ownerWindow.ResizeObserver !== 'function') {
      await nextPaint(ownerWindow);
      return check();
    }

    this.pendingVerification?.finish(false);
    return new Promise((resolve) => {
      let settled = false;
      let timer = null;
      let observer = null;
      const pending = { finish: null };
      const finish = (result) => {
        if (settled) return;
        settled = true;
        if (timer !== null) ownerWindow.clearTimeout(timer);
        observer?.disconnect();
        if (this.pendingVerification === pending) this.pendingVerification = null;
        resolve(result);
      };
      pending.finish = finish;
      this.pendingVerification = pending;
      observer = new ownerWindow.ResizeObserver(() => {
        if (!wrapper.isConnected) finish(false);
        else if (check()) finish(true);
      });
      observer.observe(wrapper, { box: 'border-box' });
      timer = ownerWindow.setTimeout(() => finish(false), INITIAL_CONTENT_RENDER_TIMEOUT_MS);

      // Close the gap between the initial check and observer registration.
      if (!wrapper.isConnected) finish(false);
      else if (check()) finish(true);
    });
  }

  destroy() { this.pendingVerification?.finish(false); }
}

class SameDocumentAdapter extends DocumentContentAdapter {
  constructor() {
    super();
    this.context = SURFACE_CONTEXT.Web;
    this.environment = 'same-document';
  }
  capabilities() {
    const size = viewportSize(window);
    return capabilities(this.context, size, flowCapacity(window), size, true, true, true, true, true);
  }
  capacity() { return viewportSize(window); }
  async prepare() { return true; }
  async expand(interaction) {
    if (interaction === SURFACE_INTERACTION.Fixed) return failed('surface-is-fixed');
    return {
      status: interaction === SURFACE_INTERACTION.ExpandOverlay
        ? SURFACE_RESULT.LocalOverlay
        : SURFACE_RESULT.Resized,
      context: this.context,
    };
  }
  async collapse() { return { status: SURFACE_RESULT.Collapsed, context: this.context }; }
}

class FriendlyFrameAdapter {
  constructor(renderEnv) {
    this.context = SURFACE_CONTEXT.Web;
    this.environment = 'friendly-iframe';
    this.renderEnv = renderEnv;
    this.frame = renderEnv.frameElement ?? readFrameElement();
    this.shell = exactGamShell(this.frame);
    this.original = captureElements(this.frame, this.shell);
    this.initial = null;
    this.expanded = false;
    this.cleanup = [];
  }

  capabilities() {
    const current = elementSize(this.frame) ?? viewportSize(window);
    const maximum = friendlyFlowCapacity(
      this.frame,
      this.shell,
      this.renderEnv.topWindow ?? window.parent,
    );
    const overlay = viewportSize(this.renderEnv.topWindow ?? window.parent);
    return capabilities(
      this.context, current, maximum, overlay, !!this.frame, false,
      !!this.frame, !!this.frame && !!this.shell, false,
    );
  }

  capacity() { return viewportSize(this.renderEnv.topWindow ?? window.parent); }

  async prepare(initial, size) {
    if (!this.frame) return false;
    if (initial === SURFACE_INITIAL.Allocated) {
      this.initial = captureElements(this.frame, this.shell);
      return sizeFits(elementSize(this.frame), size);
    }
    if (initial === SURFACE_INITIAL.Content) {
      applyDimensions(this.frame, size);
      if (this.shell) applyDimensions(this.shell, size);
    } else if (initial === SURFACE_INITIAL.Viewport) return false;
    await nextPaint(this.renderEnv.topWindow ?? window);
    this.initial = captureElements(this.frame, this.shell);
    return sizeFits(elementSize(this.frame), size) && flowContains(this.frame, this.shell, size);
  }
  verify(initial, size) {
    return initial !== SURFACE_INITIAL.Content || flowContains(this.frame, this.shell, size);
  }

  async expand(interaction, request) {
    if (!this.frame || interaction === SURFACE_INTERACTION.Fixed) return failed('surface-is-fixed');
    if (this.expanded) return failed('already-expanded');
    const beforeExpand = captureElements(this.frame, this.shell);
    const size = { width: request.width, height: request.height };
    if (interaction === SURFACE_INTERACTION.ExpandPush) {
      applyDimensions(this.frame, size);
      if (this.shell) applyDimensions(this.shell, size);
    } else {
      if (!this.shell) return failed('friendly-overlay-requires-verified-shell');
      applyDimensions(this.frame, size);
      applyStyles(this.frame, {
        position: 'absolute',
        top: `${-request.offsets.top}px`,
        left: `${-request.offsets.left}px`,
        right: 'auto',
        bottom: 'auto',
        zIndex: '2147483646',
      });
      // Establish the verified GAM shell as the containing block. Without
      // this, absolute top/left offsets can resolve against an unrelated
      // publisher ancestor when GAM leaves its shell position:static.
      applyStyles(this.shell, { overflow: 'visible', position: 'relative' });
    }
    await nextPaint(this.renderEnv.topWindow ?? window);
    const achieved = sizeFits(elementSize(this.frame), size)
      && (interaction !== SURFACE_INTERACTION.ExpandPush
        || flowContains(this.frame, this.shell, size));
    if (!achieved) {
      restoreElements(beforeExpand, this.frame, this.shell);
      return failed('friendly-resize-not-achieved');
    }
    this.initial = beforeExpand;
    this.expanded = true;
    return { status: SURFACE_RESULT.Resized, context: this.context };
  }

  async collapse() {
    if (!this.expanded || !this.initial) return failed('not-expanded');
    restoreElements(this.initial, this.frame, this.shell);
    this.expanded = false;
    return { status: SURFACE_RESULT.Collapsed, context: this.context };
  }

  bindLifecycle(onInvalidated) {
    const topWindow = this.renderEnv.topWindow;
    if (!topWindow || !this.frame) return;
    const observer = new topWindow.MutationObserver(() => {
      if (!this.frame.isConnected) onInvalidated();
    });
    observer.observe(topWindow.document.documentElement, { childList: true, subtree: true });
    this.cleanup.push(() => observer.disconnect());

    const slotId = this.shell?.parentElement?.id;
    const googletag = topWindow.googletag;
    if (!slotId || !googletag?.cmd) return;
    googletag.cmd.push(() => {
      const service = googletag.pubads?.();
      if (!service?.addEventListener) return;
      const listener = event => {
        if (event.slot?.getSlotElementId?.() === slotId) onInvalidated();
      };
      service.addEventListener('slotRequested', listener);
      this.cleanup.push(() => service.removeEventListener?.('slotRequested', listener));
    });
  }

  destroy() {
    for (const cleanup of this.cleanup.splice(0)) cleanup();
    restoreElements(this.original, this.frame, this.shell);
  }
}

class SafeFrameAdapter {
  constructor() {
    this.context = SURFACE_CONTEXT.Web;
    this.environment = 'safeframe';
    this.ext = window.$sf?.ext;
    this.pending = null;
    this.registered = false;
    this.expanded = false;
    this.listeners = new Set();
  }

  capabilities() {
    const support = safeFrameSupport(this.ext);
    const geom = safeCall(() => this.ext?.geom?.()) ?? {};
    const current = safeFrameCurrentSize(geom) ?? viewportSize(window);
    return capabilities(
      this.context, current, safeFrameMaximumSize(geom, current),
      safeFrameMaximumSize(geom, current), false, false,
      support.push, support.overlay, false,
    );
  }

  capacity() { return this.capabilities().maximum_size; }
  async prepare(initial, size) {
    await this.register(size);
    return initial === SURFACE_INITIAL.Allocated && sizeFits(this.capabilities().current_size, size);
  }

  async register(size) {
    if (this.registered || typeof this.ext?.register !== 'function') return;
    this.ext.register(size.width, size.height, (status, data) => {
      const normalized = String(status ?? '').toLowerCase();
      if (normalized.includes('expanded')) {
        this.expanded = true;
        this.emitState('expanded');
      }
      if (normalized.includes('collapsed')) {
        this.expanded = false;
        this.emitState('collapsed');
      }
      this.pending?.resolve({ status: normalized, data });
    });
    this.registered = true;
  }

  async expand(interaction, request) {
    const push = interaction === SURFACE_INTERACTION.ExpandPush;
    const support = safeFrameSupport(this.ext);
    if (interaction === SURFACE_INTERACTION.Fixed) return failed('surface-is-fixed');
    if ((push && !support.push) || (!push && !support.overlay)) return failed('safeframe-mode-unsupported');
    if (typeof this.ext?.expand !== 'function') return failed('safeframe-expand-unavailable');
    try {
      const confirmation = this.waitForStatus('expand');
      this.ext.expand({ ...clampSafeFrameOffsets(request.offsets, safeCall(() => this.ext.geom?.())?.exp), push });
      const event = await confirmation;
      if (!event.status.includes('expand')) return failed(`safeframe-${event.status || 'failed'}`);
      this.expanded = true;
      return { status: SURFACE_RESULT.Expanded, context: this.context };
    } catch (error) {
      this.pending?.cancel();
      return failed(`safeframe-expand-error:${messageOf(error)}`);
    }
  }

  async collapse() {
    if (!this.expanded || typeof this.ext?.collapse !== 'function') return failed('not-expanded');
    try {
      const confirmation = this.waitForStatus('collapse');
      this.ext.collapse();
      const event = await confirmation;
      if (!event.status.includes('collapse')) return failed(`safeframe-${event.status || 'failed'}`);
      this.expanded = false;
      return { status: SURFACE_RESULT.Collapsed, context: this.context };
    } catch (error) {
      this.pending?.cancel();
      return failed(`safeframe-collapse-error:${messageOf(error)}`);
    }
  }

  waitForStatus(expected) {
    this.pending?.reject(new Error('operation-superseded'));
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending = null;
        reject(new Error('confirmation-timeout'));
      }, OPERATION_TIMEOUT_MS);
      this.pending = {
        resolve: value => {
          if (!value.status.includes(expected)
            && !value.status.includes('error')
            && !value.status.includes('fail')) return;
          clearTimeout(timer);
          this.pending = null;
          resolve(value);
        },
        reject: error => { clearTimeout(timer); this.pending = null; reject(error); },
        cancel: () => { clearTimeout(timer); this.pending = null; },
      };
    });
  }

  emitState(state) {
    for (const listener of this.listeners) listener({ state });
  }
  onStateChange(listener) { this.listeners.add(listener); return () => this.listeners.delete(listener); }

  destroy() {
    this.pending?.reject(new Error('surface-destroyed'));
    this.pending = null;
    this.listeners.clear();
    if (this.expanded) safeCall(() => this.ext?.collapse?.());
  }
}

class MraidAdapter {
  constructor() {
    this.mraid = window.mraid;
    this.context = SURFACE_CONTEXT.InAppInline;
    this.environment = 'mraid';
    this.placementType = 'inline';
    this.expanded = false;
    this.listeners = new Set();
    this.cleanup = [];
  }

  async ready() {
    if (!this.mraid) return;
    if (safeCall(() => this.mraid.getState?.()) === 'loading')
      await waitForSdkEvent(this.mraid, 'ready', OPERATION_TIMEOUT_MS);
    this.placementType = safeCall(() => this.mraid.getPlacementType?.()) ?? 'inline';
    this.context = this.placementType === MRAID_INTERSTITIAL
      ? SURFACE_CONTEXT.InAppInterstitial
      : SURFACE_CONTEXT.InAppInline;
    const onState = state => {
      this.expanded = state === 'expanded';
      for (const listener of this.listeners)
        listener({ state: this.expanded ? 'expanded' : 'collapsed' });
    };
    this.mraid.addEventListener?.('stateChange', onState);
    this.cleanup.push(() => this.mraid.removeEventListener?.('stateChange', onState));
  }

  capabilities() {
    const current = normalizeSdkSize(safeCall(() => this.mraid?.getCurrentPosition?.()))
      ?? normalizeSdkSize(safeCall(() => this.mraid?.getDefaultPosition?.()))
      ?? viewportSize(window);
    const maximum = normalizeSdkSize(safeCall(() => this.mraid?.getScreenSize?.()))
      ?? normalizeSdkSize(safeCall(() => this.mraid?.getMaxSize?.()))
      ?? current;
    const interstitial = this.placementType === MRAID_INTERSTITIAL;
    return capabilities(
      this.context, current, maximum, maximum, false, interstitial, false,
      !interstitial && typeof this.mraid?.expand === 'function', false,
    );
  }

  capacity() { return this.capabilities().maximum_size; }
  async prepare(initial, size) {
    if (initial === SURFACE_INITIAL.Viewport) return this.placementType === MRAID_INTERSTITIAL;
    return initial === SURFACE_INITIAL.Allocated && sizeFits(this.capabilities().current_size, size);
  }

  async expand(interaction, request) {
    if (this.placementType === MRAID_INTERSTITIAL) return failed('mraid-interstitial-already-fullscreen');
    if (interaction !== SURFACE_INTERACTION.ExpandOverlay) return failed('mraid-overlay-only');
    if (typeof this.mraid?.expand !== 'function') return failed('mraid-expand-unavailable');
    try {
      this.mraid.setExpandProperties?.({ width: request.width, height: request.height, useCustomClose: false });
      const confirmation = waitForMraidState(this.mraid, state => state === 'expanded');
      try { this.mraid.expand(); }
      catch (error) { confirmation.cancel(); throw error; }
      await confirmation.promise;
      this.expanded = true;
      return { status: SURFACE_RESULT.Expanded, context: this.context };
    } catch (error) {
      return failed(`mraid-expand-error:${messageOf(error)}`);
    }
  }

  async collapse() {
    if (!this.expanded || typeof this.mraid?.close !== 'function') return failed('not-expanded');
    try {
      const confirmation = waitForMraidState(this.mraid, state => state !== 'expanded');
      try { this.mraid.close(); }
      catch (error) { confirmation.cancel(); throw error; }
      await confirmation.promise;
      this.expanded = false;
      return { status: SURFACE_RESULT.Collapsed, context: this.context };
    } catch (error) {
      return failed(`mraid-collapse-error:${messageOf(error)}`);
    }
  }

  onStateChange(listener) { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  destroy() {
    for (const cleanup of this.cleanup.splice(0)) cleanup();
    this.listeners.clear();
    if (this.expanded) safeCall(() => this.mraid?.close?.());
  }
}

class BlockedAdapter extends DocumentContentAdapter {
  constructor() {
    super();
    this.context = SURFACE_CONTEXT.Web;
    this.environment = 'blocked';
  }
  capabilities() {
    const size = viewportSize(window);
    return capabilities(this.context, size, size, size, false, false, false, false, false);
  }
  capacity() { return viewportSize(window); }
  async prepare(initial, size) {
    const ownsExistingSurface = initial === SURFACE_INITIAL.Allocated
      || initial === SURFACE_INITIAL.Content;
    return ownsExistingSurface && sizeFits(this.capacity(), size);
  }
  async expand() { return failed('surface-blocked'); }
  async collapse() { return failed('surface-blocked'); }
}

function capabilities(context, current, maximum, overlaySize, resize, viewport, push, overlay, topFrame) {
  const normalizedMaximum = {
    width: Math.max(current.width, maximum.width),
    height: Math.max(current.height, maximum.height),
  };
  return {
    context,
    current_size: current,
    maximum_size: normalizedMaximum,
    overlay_size: {
      width: Math.max(current.width, overlaySize.width),
      height: Math.max(current.height, overlaySize.height),
    },
    supports_content_resize: resize,
    supports_viewport: viewport,
    supports_push_expansion: push,
    supports_overlay_expansion: overlay,
    top_frame: topFrame,
  };
}

function validExpansionRequest(request) {
  return isPositiveSize(request) && request.offsets
    && ['top', 'right', 'bottom', 'left'].every(
      edge => Number.isFinite(request.offsets[edge]) && request.offsets[edge] >= 0,
    );
}

function failed(reason) { return { status: SURFACE_RESULT.Failed, reason }; }
function messageOf(error) { return error instanceof Error ? error.message : String(error); }
function safeCall(fn) { try { return fn(); } catch (_) { return null; } }

function viewportSize(targetWindow) {
  const width = firstPositive(
    targetWindow?.visualViewport?.width,
    targetWindow?.document?.documentElement?.clientWidth,
    targetWindow?.innerWidth,
  );
  const height = firstPositive(
    targetWindow?.visualViewport?.height,
    targetWindow?.document?.documentElement?.clientHeight,
    targetWindow?.innerHeight,
  );
  return { width: Math.max(1, Math.floor(width)), height: Math.max(1, Math.floor(height)) };
}

function flowCapacity(targetWindow) {
  const viewport = viewportSize(targetWindow);
  return { width: viewport.width, height: Math.max(viewport.height, MAX_FLOW_SURFACE_HEIGHT_PX) };
}

function friendlyFlowCapacity(frame, shell, targetWindow) {
  const viewport = viewportSize(targetWindow);
  const owner = shell?.parentElement ?? frame?.parentElement;
  const ownerWidth = owner?.getBoundingClientRect?.().width;
  return {
    width: Number.isFinite(ownerWidth) && ownerWidth > 0
      ? Math.floor(ownerWidth)
      : viewport.width,
    height: Math.max(viewport.height, MAX_FLOW_SURFACE_HEIGHT_PX),
  };
}

function firstPositive(...values) {
  return values.find(value => Number.isFinite(value) && value > 0) ?? 1;
}

function elementSize(element) {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  const width = Math.round(rect.width || element.offsetWidth || Number(element.width) || 0);
  const height = Math.round(rect.height || element.offsetHeight || Number(element.height) || 0);
  return width > 0 && height > 0 ? { width, height } : null;
}

function readFrameElement() {
  try { return window.frameElement?.tagName === 'IFRAME' ? window.frameElement : null; }
  catch (_) { return null; }
}

function exactGamShell(frame) {
  const shell = frame?.parentElement;
  return frame?.id?.startsWith('google_ads_iframe_') && shell?.id === `${frame.id}__container__`
    ? shell : null;
}

function captureElement(element) {
  if (!element) return null;
  const styles = {};
  for (const prop of MANAGED_STYLE_PROPS) styles[prop] = element.style[prop];
  return { styles, width: element.getAttribute('width'), height: element.getAttribute('height') };
}
function captureElements(frame, shell) { return { frame: captureElement(frame), shell: captureElement(shell) }; }
function restoreElement(snapshot, element) {
  if (!snapshot || !element) return;
  for (const prop of MANAGED_STYLE_PROPS) element.style[prop] = snapshot.styles[prop];
  restoreAttribute(element, 'width', snapshot.width);
  restoreAttribute(element, 'height', snapshot.height);
}
function restoreElements(snapshot, frame, shell) {
  if (!snapshot) return;
  restoreElement(snapshot.frame, frame);
  restoreElement(snapshot.shell, shell);
}
function restoreAttribute(element, name, value) {
  if (value === null) element.removeAttribute(name); else element.setAttribute(name, value);
}
function applyDimensions(element, size) {
  applyStyles(element, {
    width: `${size.width}px`, height: `${size.height}px`, maxWidth: 'none', maxHeight: 'none',
    minWidth: '0px', minHeight: '0px',
  });
  if (element.tagName === 'IFRAME') {
    element.width = String(size.width);
    element.height = String(size.height);
  }
}
function applyStyles(element, styles) {
  for (const [name, value] of Object.entries(styles)) element.style[name] = value;
}
function nextPaint(targetWindow) {
  return new Promise(resolve => {
    if (typeof targetWindow.requestAnimationFrame === 'function')
      targetWindow.requestAnimationFrame(() => resolve());
    else
      targetWindow.setTimeout(resolve, 0);
  });
}

function safeFrameSupport(ext) {
  const raw = safeCall(() => ext?.supports?.()) ?? {};
  return {
    overlay: raw['exp-ovr'] === true,
    push: raw['exp-push'] === true,
  };
}
function safeFrameCurrentSize(geom) {
  const self = geom?.self;
  if (!self) return null;
  const width = self.w ?? Math.max(0, (self.r ?? 0) - (self.l ?? 0));
  const height = self.h ?? Math.max(0, (self.b ?? 0) - (self.t ?? 0));
  return width > 0 && height > 0 ? { width, height } : null;
}
function safeFrameMaximumSize(geom, current) {
  const exp = geom?.exp;
  return exp ? {
    width: current.width + availableOffset(exp.l) + availableOffset(exp.r),
    height: current.height + availableOffset(exp.t) + availableOffset(exp.b),
  } : current;
}
function clampSafeFrameOffsets(offsets, permitted = {}) {
  return {
    t: Math.min(offsets.top, availableOffset(permitted.t, offsets.top)),
    r: Math.min(offsets.right, availableOffset(permitted.r, offsets.right)),
    b: Math.min(offsets.bottom, availableOffset(permitted.b, offsets.bottom)),
    l: Math.min(offsets.left, availableOffset(permitted.l, offsets.left)),
  };
}
function availableOffset(value, fallback = 0) {
  return Number.isFinite(value) ? Math.abs(value) : fallback;
}

function flowContains(frame, shell, size) {
  const owner = shell?.parentElement ?? frame?.parentElement;
  if (!owner) return false;
  for (let element = owner; element && element !== element.ownerDocument.body; element = element.parentElement) {
    const rect = element.getBoundingClientRect();
    const style = element.ownerDocument.defaultView?.getComputedStyle?.(element);
    const clipsX = ['hidden', 'clip', 'scroll', 'auto']
      .includes(style?.overflowX || style?.overflow);
    const clipsY = ['hidden', 'clip', 'scroll', 'auto']
      .includes(style?.overflowY || style?.overflow);
    if ((clipsX && rect.width < size.width) || (clipsY && rect.height < size.height)) return false;
  }
  const rect = owner.getBoundingClientRect();
  return rect.width >= size.width && rect.height >= size.height;
}

function hasClippingAncestor(element, size) {
  for (let parent = element.parentElement;
    parent && parent !== parent.ownerDocument.body;
    parent = parent.parentElement) {
    const rect = parent.getBoundingClientRect();
    const style = parent.ownerDocument.defaultView?.getComputedStyle?.(parent);
    if (['hidden', 'clip', 'auto', 'scroll'].includes(style?.overflowX || style?.overflow)
      && rect.width < size.width) return true;
    if (['hidden', 'clip', 'auto', 'scroll'].includes(style?.overflowY || style?.overflow)
      && rect.height < size.height) return true;
  }
  return false;
}

function responsiveContentSize(preferred, available) {
  if (!isPositiveSize(preferred) || !isPositiveSize(available)
    || available.width < MIN_CONTENT_SURFACE_WIDTH_PX) return null;
  return {
    width: Math.min(preferred.width, available.width),
    height: preferred.height,
  };
}
function normalizeSdkSize(value) {
  return Number.isFinite(value?.width) && Number.isFinite(value?.height)
    && value.width > 0 && value.height > 0
    ? { width: Math.floor(value.width), height: Math.floor(value.height) } : null;
}

function waitForSdkEvent(sdk, event, timeoutMs) {
  return new Promise((resolve, reject) => {
    const finish = (callback, value) => {
      clearTimeout(timer);
      sdk.removeEventListener?.(event, listener);
      callback(value);
    };
    const listener = value => finish(resolve, value);
    const timer = setTimeout(() => finish(reject, new Error(`${event}-timeout`)), timeoutMs);
    sdk.addEventListener?.(event, listener);
  });
}
function waitForMraidState(mraid, predicate) {
  let cancel = () => {};
  const promise = new Promise((resolve, reject) => {
    const finish = (callback, value) => {
      clearTimeout(timer);
      mraid.removeEventListener?.('stateChange', onState);
      mraid.removeEventListener?.('error', onError);
      callback(value);
    };
    const onState = state => { if (predicate(state)) finish(resolve, state); };
    const onError = message => finish(reject, new Error(String(message || 'mraid-error')));
    const timer = setTimeout(() => finish(reject, new Error('state-timeout')), OPERATION_TIMEOUT_MS);
    cancel = () => {
      clearTimeout(timer);
      mraid.removeEventListener?.('stateChange', onState);
      mraid.removeEventListener?.('error', onError);
    };
    mraid.addEventListener?.('stateChange', onState);
    mraid.addEventListener?.('error', onError);
  });
  return { promise, cancel };
}
